import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { StorageKey, StorageService, DateTools } from '@treezor/design-system';

import { useMccRef } from '@/hooks/useMccRef';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { ICardTransaction } from '@/features/cards/store/models/card.model';
import { mappingIso3Countries } from '@/utils/constants';

interface IUseCardTransactionDetailsProps {
  transaction: ICardTransaction | null;
}

export function useCardTransactionDetails({ transaction }: IUseCardTransactionDetailsProps) {
  const navigate = useNavigate();
  const currentLang = StorageService.get(StorageKey.LANGUAGE);
  const mccRefs = useMccRef();

  const getIssuerDateTime = useCallback((dateString?: string) => {
    if (!dateString) return '/';
    const date = new Date(dateString);
    date.setHours(date.getHours() + 1);
    return DateTools.format(date, 'date-time');
  }, []);

  const merchantCountryCode = mappingIso3Countries?.[transaction?.merchantCountry];

  const getMccData = useCallback(
    (mccCode?: string) => {
      if (!mccCode) return null;
      const mccData = (mccRefs || []).find((mcc) => mcc.MCC.toString() === mccCode.toString());
      if (mccData) {
        return {
          code: mccCode,
          label: mccData[currentLang === 'fr' ? 'DescriptionFR' : 'DescriptionEN'],
        };
      }
      return { code: mccCode };
    },
    [mccRefs, currentLang],
  );

  const handleNavigateToWallet = useCallback(
    (userId: number, walletId: number) => {
      navigate(DashboardHelpers.generateProfilePath(userId!, { walletId }));
    },
    [navigate],
  );

  const mccData = getMccData(transaction?.mccCode);

  return {
    getIssuerDateTime,
    merchantCountryCode,
    getMccData,
    handleNavigateToWallet,
    mccData,
  };
}
